import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import Navbar from '../../../../shared/components/Navbar';
import BackgroundImage from '../../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../../shared/components/ParticlesBackground';
import Card from '../../../../shared/components/Card';
import AllianceParams, { AllianceSettingsData } from '../components/AllianceParams';
import AllianceMembers from '../components/AllianceMembers';
import AllianceWall from '../components/AllianceWall';
import AllianceFormations from '../components/AllianceFormations';
import AllianceCRMs from '../components/AllianceCRMs';
import AllianceBank from '../components/AllianceBank';
import AllianceBuildings from '../components/AllianceBuildings';
import AllianceCandidates from '../components/AllianceCandidates';
import AllianceLogs from '../components/AllianceLogs';
import AllianceListView, { AllianceListItem } from '../components/AllianceListView';
import CreateAllianceModal, { CreateAllianceForm } from '../components/CreateAllianceModal';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export type AllianceSectionKey =
  | 'presentation'
  | 'members'
  | 'rules'
  | 'wall'
  | 'training'
  | 'crms'
  | 'bank'
  | 'buildings'
  | 'candidates'
  | 'logs'
  | 'params';

const defaultSettings: AllianceSettingsData = {
  name: '',
  slogan: '',
  presentationText: '',
  discordLink: '',
  rulesText: '',
  acceptanceRules: '',
  minLevel: 1,
  allowBeginners: true,
};

const mockAlliances: AllianceListItem[] = [
  { id: '1', name: 'Global Rescue', slogan: "L'entraide et la bonne humeur !", memberCount: 42 },
  { id: '2', name: 'Pompiers Unis', slogan: 'Solidarité et réactivité.', memberCount: 28 },
  { id: '3', name: 'Secours 24/7', slogan: 'Toujours prêts, partout.', memberCount: 15 },
];

const AlliancePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [userAllianceId, setUserAllianceId] = useState<string | null>(null);

  // Feature Alliance en cours de développement — accessible à tous en lecture "bientôt disponible"
  const allianceComingSoon = true; // Retirer ce flag quand les API backend sont branchées
  const [previewAllianceId, setPreviewAllianceId] = useState<string | null>(null);
  const [alliancesList, setAlliancesList] = useState<AllianceListItem[]>(mockAlliances);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [section, setSection] = useState<AllianceSectionKey>('presentation');
  const [allianceSettings, setAllianceSettings] = useState<AllianceSettingsData>({
    ...defaultSettings,
    name: 'Global Rescue',
    slogan: "L'entraide et la bonne humeur !",
    presentationText: "Tous les profils sont acceptés, débutants, confirmés. Venez passer de bons moments avec nous !",
    rulesText: "Compte : compte enregistré requis. Tous les grades acceptés. Doubles comptes et pseudos déplacés interdits.\n\nCotisation : 1 % minimum à la banque de l'alliance dès l'arrivée.\n\nPartages de missions : missions >= 20 000 € peuvent être partagées dans le tchat. Partager des missions < 5 000 € est interdit.",
    acceptanceRules: "Compte valide avec pseudo requis. Doubles comptes interdits. Cotisation 1% obligatoire.",
  });
  const isAllianceAdmin = true;

  const handleJoin = (id: string) => {
    const alliance = alliancesList.find((a) => a.id === id);
    if (alliance) {
      setAllianceSettings((prev) => ({
        ...defaultSettings,
        ...prev,
        name: alliance.name,
        slogan: alliance.slogan,
        presentationText: alliance.slogan,
        discordLink: '',
        rulesText: '',
      }));
      setUserAllianceId(id);
    }
  };

  const handleCreateAlliance = (form: CreateAllianceForm) => {
    const id = `new-${Date.now()}`;
    setAlliancesList((prev) => [
      ...prev,
      { id, name: form.name, slogan: form.slogan, memberCount: 1, presentationText: form.presentationText || form.slogan },
    ]);
    setAllianceSettings((prev) => ({
      ...prev,
      name: form.name,
      slogan: form.slogan,
      presentationText: form.presentationText || form.slogan,
    }));
    setUserAllianceId(id);
  };

  const handleLeave = () => setUserAllianceId(null);

  const previewAlliance = previewAllianceId ? alliancesList.find((a) => a.id === previewAllianceId) : null;

  const navItems: { key: AllianceSectionKey; labelKey: string; adminOnly?: boolean }[] = useMemo(
    () => [
      { key: 'presentation', labelKey: 'nav.presentation' },
      { key: 'members', labelKey: 'nav.members' },
      { key: 'rules', labelKey: 'nav.rules' },
      { key: 'wall', labelKey: 'nav.wall' },
      { key: 'training', labelKey: 'nav.training' },
      { key: 'crms', labelKey: 'nav.crms' },
      { key: 'bank', labelKey: 'nav.bank' },
      { key: 'buildings', labelKey: 'nav.buildings' },
      { key: 'candidates', labelKey: 'nav.candidates' },
      { key: 'logs', labelKey: 'nav.logs' },
      { key: 'params', labelKey: 'nav.params', adminOnly: true },
    ],
    []
  );

  const visibleNavItems = navItems.filter((item) => !item.adminOnly || isAllianceAdmin);

  const renderDashboardContent = () => {
    switch (section) {
      case 'presentation':
        return (
          <Card className="p-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-40 h-40 rounded-full bg-gray-700/80 border-4 border-indigo-500/50 flex items-center justify-center overflow-hidden">
                  <svg className="w-20 h-20 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500 mt-2 text-center">{t('presentation.logoLabel')}</p>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-indigo-400 mb-4">{allianceSettings.slogan || t('presentation.sloganDefault')}</h2>
                <p className="text-gray-300 whitespace-pre-line">{allianceSettings.presentationText || t('comingSoon')}</p>
                {allianceSettings.discordLink?.trim() && (
                  <p className="mt-4">
                    <a
                      href={allianceSettings.discordLink.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                      </svg>
                      {t('presentation.discordLink')}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </Card>
        );
      case 'members':
        return <AllianceMembers />;
      case 'wall':
        return <AllianceWall />;
      case 'training':
        return <AllianceFormations />;
      case 'crms':
        return <AllianceCRMs />;
      case 'bank':
        return <AllianceBank />;
      case 'buildings':
        return <AllianceBuildings />;
      case 'candidates':
        return <AllianceCandidates />;
      case 'logs':
        return <AllianceLogs />;
      case 'rules':
        return (
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-white mb-6">{t('rules.title')}</h2>
            <div className="text-gray-300 whitespace-pre-line">
              {allianceSettings.rulesText || t('rules.empty')}
            </div>
            {allianceSettings.discordLink?.trim() && (
              <p className="mt-6 pt-4 border-t border-gray-700">
                <span className="text-gray-400">{t('rules.discordLabel')} </span>
                <a
                  href={allianceSettings.discordLink.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 font-medium"
                >
                  {allianceSettings.discordLink.trim()}
                </a>
              </p>
            )}
          </Card>
        );
      case 'params':
        return (
          <AllianceParams
            initialData={allianceSettings}
            isAdmin={isAllianceAdmin}
            onSave={(data) => {
              setAllianceSettings(data);
              if (userAllianceId) {
                setAlliancesList((prev) =>
                  prev.map((a) => (a.id === userAllianceId ? { ...a, name: data.name, presentationText: data.presentationText, discordLink: data.discordLink } : a))
                );
              }
            }}
            existingNames={alliancesList.filter((a) => a.id !== userAllianceId).map((a) => a.name.toLowerCase())}
          />
        );
      default:
        return (
          <Card className="p-8">
            <p className="text-gray-400 text-center py-8">{t('comingSoon')}</p>
          </Card>
        );
    }
  };

  const inAlliance = userAllianceId !== null;
  const isCm = currentUser?.roles_id === 1;

  if (!currentUser || !isCm) {
    return null;
  }

  // ── Bêta fermée : Alliances en cours de développement ─────────────────────
  if (allianceComingSoon) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <BackgroundImage />
        <ParticlesBackground />
        <Navbar onSupportClick={() => {}} />
        <div className="relative z-10 pt-24 pb-16 flex flex-col items-center justify-center min-h-[70vh]">
          <div className="max-w-lg mx-auto px-4 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="mb-8 flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors mx-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Retour au tableau de bord</span>
            </button>
            <div className="text-6xl mb-6">🤝</div>
            <h1 className="text-3xl font-bold text-white mb-4">Alliances</h1>
            <p className="text-xl text-indigo-300 font-semibold mb-4">Bientôt disponible</p>
            <p className="text-gray-400 mb-8">
              Le système d'Alliances est en cours de développement et sera disponible dans une prochaine mise à jour.
              Rejoignez notre Discord pour suivre l'avancement !
            </p>
            <a
              href="https://discord.gg/7wVa8EkvnP"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Rejoindre le Discord
            </a>
          </div>
        </div>
      </div>
    );
  }
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar onSupportClick={() => {}} />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>{t('backToDashboard')}</span>
          </button>

          <Card className="mb-6 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg">
            <p className="text-amber-200 font-semibold text-center">{t('devWarning')}</p>
          </Card>

          {!inAlliance ? (
            previewAlliance ? (
              <Card className="p-8">
                <div className="flex flex-col gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setPreviewAllianceId(null)}
                    className="self-start flex items-center gap-2 text-gray-400 hover:text-white"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('list.backToList')}
                  </button>
                  <h1 className="text-2xl font-bold text-white">{previewAlliance.name}</h1>
                </div>
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="w-40 h-40 rounded-full bg-gray-700/80 border-4 border-indigo-500/50 flex items-center justify-center">
                      <svg className="w-20 h-20 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-indigo-400 mb-4">{previewAlliance.slogan}</h2>
                    <p className="text-gray-300 whitespace-pre-line">{previewAlliance.presentationText || previewAlliance.slogan}</p>
                    {previewAlliance.discordLink?.trim() && (
                      <p className="mt-4">
                        <a
                          href={previewAlliance.discordLink.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                          </svg>
                          {t('presentation.discordLink')}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={() => { handleJoin(previewAlliance.id); setPreviewAllianceId(null); }}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
                  >
                    {t('list.join')}
                  </button>
                </div>
              </Card>
            ) : (
              <>
                <AllianceListView
                  alliances={alliancesList}
                  onJoin={handleJoin}
                  onCreateClick={() => setShowCreateModal(true)}
                  onViewPresentation={(id) => setPreviewAllianceId(id)}
                />
                <CreateAllianceModal
                  isOpen={showCreateModal}
                  onClose={() => setShowCreateModal(false)}
                  onSubmit={handleCreateAlliance}
                  existingNames={alliancesList.map((a) => a.name.toLowerCase())}
                />
              </>
            )
          ) : (
            <>
              <header className="bg-red-700/90 backdrop-blur rounded-t-lg px-6 py-4 flex items-center justify-between flex-wrap gap-4 shadow-lg border border-red-600/30">
                <h1 className="text-2xl font-bold text-white drop-shadow-sm">{allianceSettings.name || t('title')}</h1>
                <button
                  type="button"
                  onClick={handleLeave}
                  className="px-5 py-2.5 bg-red-800 hover:bg-red-900 active:brightness-90 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-red-900 border border-red-600/50"
                >
                  {t('leaveAlliance')}
                </button>
              </header>

              <nav className="bg-gray-800/90 border-x border-b border-gray-700 rounded-b-lg overflow-hidden shadow-md">
                <div className="flex flex-wrap gap-2 p-3">
                  {visibleNavItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSection(item.key)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                        section === item.key
                          ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-400/50 focus:ring-indigo-500'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white focus:ring-gray-500 active:scale-[0.98]'
                      }`}
                    >
                      {t(item.labelKey)}
                    </button>
                  ))}
                </div>
              </nav>

              <div className="mt-6">{renderDashboardContent()}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlliancePage;
